import { getFormValid, uiActions } from '../slices'

import {
	isCarryingMember,
	translateNominal,
	getFloorTrussValue,
	isCalculationAction,
} from '../../common/fns'
import { MEMBER_TYPE } from '../../common/constants'
import { numberFormatted } from '../../../../utils/fns'
import debounce from 'lodash/debounce'

const MID_WALL_ADDITIONAL_HEIGHT = 12;

const transformMemberDimension = (member, memberType) => {
	const { type, width, depth, isRough } = member

	switch (type) {
		case 'solidSawn':
		case 'ledger': {
			const nominalValue = translateNominal(width, depth, isRough)

			return { ...member, ...nominalValue }
		}
		case 'floorTruss': {
			if (isCarryingMember(memberType)) {
				const floorTrussValue = getFloorTrussValue(width)

				return { ...member, ...floorTrussValue }
			} else return { ...member }
		}
		case 'ssteel': {
			return { ...member, depth: 5.5 }
		}
		case 'nailer':
		case 'wallSFlush':
		case 'wallSGap':
		case 'wallDFlush':
		case 'wallDFlushStud':
		case 'wallDGap': {
			const swappedValue = {
				thickness: type.includes('wall') ? 1.5 : width,
				width: depth,
				depth: 5.5,
			}

			return { ...member, ...swappedValue }
		}
		default: {
			return { ...member }
		}
	}
}

const calculationGraphicData = ({ dispatch, getState }) => {
	const input = getState().calculation.input

	const member = {
		header: input.header,
		girder: input.girder,
		joist: input.joist,
		truss: input.truss,
		jack: input.jack,
		leftHip: input.leftHip,
		rightHip: input.rightHip,
	}

	const { configuration, hangerType } = input.jobSettings
	const connectionType = input.settings.connectionType
	const hangerOptions = input.hangerOptions

	const { header, girder, joist, truss, jack, leftHip, rightHip } = Object.keys(member).reduce(
		(acc, key) => {
			const {
				type,
				material,
				width,
				depth,
				ply,
				topChord,
				thickness,
				kingWidth,
				slopeAngle,
				skewAngle,
			} = transformMemberDimension(member[key], key)

			const _hasFormatHeight = [
				'ledger',
				'nailer',
				'wallSGap',
				'wallSFlush',
				'wallDGap',
				'wallDFlush',
				'wallDFlushStud',
			].includes(type)

			const nailerTotalThick = ply * (thickness ?? 0.5)
			const _carryingHeight = _hasFormatHeight ? nailerTotalThick + depth : depth

			if (isCarryingMember(key)) {
				acc[key] = {
					_carryingType: type,
					_carryingMaterial: material,
					_carryingWidth: width,
					_carryingHeight,
					_headerPly: ply,
					_headerFlangeThick: thickness,
					_carryingSlope: -hangerOptions.topFlangeSlopedDownAngle,
					_numberOfTopChord: topChord,
					_trussVertWidth: kingWidth ?? 0,
				}
			} else {
				const getCarriedData = {
					default: {
						_carriedType: type,
						_carriedMaterial: material,
						_carriedWidth: width,
						_carriedHeight: depth,
						_joistPly: ply,
						_joistFlangeThick: thickness,
					},
					jack: {
						_centerTrussType: type,
						_centerTrussMaterial: material,
						_centerTrussWidth: width,
						_centerTrussHeel: depth,
						_centerTrussSlope: -slopeAngle,
						_centerTrussSkew: 0,
						_centerTrussPly: ply,
					},
					leftHip: {
						_leftTrussType: type,
						_leftTrussMaterial: material,
						_leftTrussWidth: width,
						_leftTrussHeel: depth,
						_leftTrussSlope: -slopeAngle,
						_leftTrussSkew: skewAngle,
						_leftTrussPly: ply,
					},
					rightHip: {
						_rightTrussType: type,
						_rightTrussMaterial: material,
						_rightTrussWidth: width,
						_rightTrussHeel: depth,
						_rightTrussSlope: -slopeAngle,
						_rightTrussSkew: skewAngle,
						_rightTrussPly: ply,
					},
				}

				acc[key] = getCarriedData[key] ?? getCarriedData.default
			}

			return acc
		},
		{},
	)

	const hanger = {
		_hangerType: hangerType,
		_open: hangerOptions.topFlangeOpenClosedAngle,
		_offset: hangerOptions.topFlangeOffset,
		_flush: hangerType === 1 || hangerType === 3 ? 1 : hangerOptions.hlcFlush,
		_slope: -hangerOptions.slopeAngle,
		_skew: -hangerOptions.skewAngle,
	}

	const multiTrussConfig = Object.assign(
		{},
		{
			_trussType: configuration.includes('center') ? jack._centerTrussType : leftHip._leftTrussType,
			_trussMaterial: configuration.includes('center')
				? jack._centerTrussType
				: leftHip._leftTrussType,
			_trussSkew: configuration.includes('left')
				? leftHip._leftTrussSkew
				: rightHip._rightTrussSkew,
		},
	)

	// Format Header member values when member type is Masonry
	if (header._carryingType === MEMBER_TYPE.MASONRY) {
		header._carryingType = MEMBER_TYPE.MASONRY_MID_WALL
		header._carryingHeight = joist._carriedHeight + MID_WALL_ADDITIONAL_HEIGHT;
	}

	if (girder._carryingType === MEMBER_TYPE.MASONRY) {
		girder._carryingType = MEMBER_TYPE.MASONRY_TOP_WALL
	}

	const memberData = {
		joist: {
			_connectionType: connectionType,
			carrying: header,
			carried: joist,
			hanger,
		},
		truss: {
			_connectionType: connectionType,
			carrying: girder,
			carried: truss,
			hanger,
		},
		multiTruss: {
			_verticalWidth: 0,
			_configuration: configuration,
			_connectionType: connectionType,
			carrying: girder,
			carried: {
				centerTruss: jack,
				leftTruss: leftHip,
				rightTruss: rightHip,
				trussConfig: multiTrussConfig,
			},
			hanger,
		},
	}

	const formattedData = numberFormatted(memberData)

	dispatch(
		uiActions.setGraphicsData({
			hasExported: true,
			...(formattedData[connectionType] ?? formattedData.joist),
		}),
	)
}

const debouncingCalculation = debounce(calculationGraphicData, 500)

const createGraphicDataLegacy = (store) => (next) => (action) => {
	next(action)

	const isValid = getFormValid(store.getState())
	if (isCalculationAction(action.type) && isValid) {
		debouncingCalculation(store)
	}
}

export default createGraphicDataLegacy
